import type { Logger } from '@mimir/common/types/logger';
import type { AsyncTransmuxer, AsyncTransmuxerOutput } from '@mimir/client-common/mse/asyncTransmuxer';
import { createUuid } from '@mimir/common/util/createUuid';
import type { WebWorkerAsyncTransmuxerRequest, WebWorkerAsyncTransmuxerResponse } from './webWorkerAsyncTransmuxerProtocol';

export function createWebWorkerAsyncTransmuxer(abortSignal: AbortSignal, logger: Logger): AsyncTransmuxer {
  interface Pending {
    resolve: (result: AsyncTransmuxerOutput) => void,
    reject: (err: any) => void,
  }

  const callbacks : Record<string, Pending> = {};
  const worker = new Worker(new URL('./webWorkerAsyncTransmuxerProcessor.ts', import.meta.url), {
    type: 'module',
  });

  abortSignal.addEventListener('abort', () => worker.terminate());

  worker.onmessage = (event: MessageEvent<WebWorkerAsyncTransmuxerResponse>) => {
    const message = event.data;

    const callback = callbacks[message.messageId];
    delete callbacks[message.messageId];
    if (callback) {
      if (message.result.type === 'success') {
        logger.silly(() => 'Transmuxed from web worker: OK');
        callback.resolve(message.result.output);
      } else {
        callback.reject(new Error(`Error message from WebWorker: ${message.result.errorMsg}`));
      }
    } else {
      logger.error(`Unexpected message: ${JSON.stringify(message)}`);
    }
  };

  return async (
    playerId: string,
    m2tsSegment: Uint8Array,
    flacDownmixMatrix: number[][] | null,
    onlyUseLastAacPacket: boolean,
  ): Promise<AsyncTransmuxerOutput> => {
    return new Promise<AsyncTransmuxerOutput>((resolve, reject) => {
      const messageId : string = createUuid();
      callbacks[messageId] = { resolve, reject };
      const request: WebWorkerAsyncTransmuxerRequest = {
        messageId,
        playerId,
        m2tsSegment,
        flacDownmixMatrix,
        onlyUseLastAacPacket,
      };
      worker.postMessage(request);
    });
  };
}
